import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Paper,
  Typography,
  IconButton,
  Button,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import ArrowForwardIosOutlinedIcon from "@mui/icons-material/ArrowForwardIosOutlined";
import ArrowBackIosNewOutlinedIcon from "@mui/icons-material/ArrowBackIosNewOutlined";

import {
  useGetExperienceDataById,
  useSetActionCall,
  useSetProductChangeCall,
} from "../services";
import io from "socket.io-client";
import { useParams } from "react-router-dom";
import "@fontsource/urbanist";
import CollectionProductView from "./CollectionProductView";
import useSocket from "../../hooks/useSocketMessages";
import { StyledButton } from "../../Components/BottomDrawer";
import Loading from "../../Components/Loading";

const Toolbar = ({ sessionId, sendRotateCall, controlData }) => {
  const [controlStates, setControlStates] = useState({});

  useEffect(() => {
    if (controlData?.length) {
      const initialStates = {};
      controlData.forEach((control) => {
        if (control.control_data_type === "boolean") {
          initialStates[control.control_id] = control.default_value === true;
        }
      });
      setControlStates(initialStates);
    }
  }, [controlData]);

  const handleToggle = (control) => {
    const isBoolean = control.control_data_type === "boolean";
    const currentValue = controlStates?.[control.control_id] ?? false;
    const newValue = isBoolean ? !currentValue : true;

    const payload = {
      session_id: sessionId,
      message: {
        type: "control",
        message: {
          control_id: control.control_id,
          value: newValue.toString(),
        },
      },
    };

    sendRotateCall(payload);

    if (isBoolean) {
      setControlStates((prev) => ({
        ...prev,
        [control.control_id]: newValue,
      }));
    }
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: "50%",
        left: "10px",
        transform: "translateY(-50%)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "5px",
          borderRadius: "15px",
          gap: "10px",
          backgroundColor: "#EEEDEB",
        }}
      >
        {controlData?.map((control) => {
          const icon = control.control_icons.find(
            (icon) => icon.file_type === "L"
          );
          const isBoolean = control.control_data_type === "boolean";
          const isSelected = isBoolean && controlStates?.[control.control_id];

          return (
            <Box
              key={control.control_id}
              sx={{
                backgroundColor: isSelected ? "white" : "transparent",
                borderRadius: "10px",
              }}
            >
              <Tooltip title={control.control_name}>
                <IconButton onClick={() => handleToggle(control)}>
                  <img
                    src={icon?.path}
                    alt={control.control_name}
                    style={{ width: 18, height: 18 }}
                  />
                </IconButton>
              </Tooltip>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
};

const Views = ({ viewActionData, sessionId, item }) => {
  const { mutate: changeViewCall } = useSetProductChangeCall();

  const [selectedInteraction, setSelectedInteraction] = useState(null);

  const handleViewChange = (interaction) => {
    setSelectedInteraction(interaction.interaction_id);

    const viewPayload = {
      session_id: sessionId,
      message: {
        type: "run_interaction",
        message: {
          item_id: item.item_id,
          product_key: item.product.product_key,
          interaction_id: interaction.interaction_id,
        },
      },
    };

    changeViewCall(viewPayload);
  };

  return (
    <Box>
      <Paper
        elevation={3}
        sx={{
          display: "flex",
          flexDirection: "row",
          padding: "4px",
          borderRadius: "8px",
          backgroundColor: "#f0f0f0",
        }}
      >
        {viewActionData?.map((interaction) => {
          const largeIcon = interaction?.interaction_icons?.find(
            (icon) => icon.file_type === "L"
          );

          return (
            <Tooltip
              key={interaction.interaction_id}
              title={interaction.interaction_name}
            >
              <IconButton
                onClick={() => handleViewChange(interaction)}
                sx={{
                  borderRadius: "8px",
                  backgroundColor:
                    selectedInteraction === interaction.interaction_id
                      ? "white"
                      : "transparent",
                  "&:hover": {
                    backgroundColor:
                      selectedInteraction === interaction.interaction_id
                        ? "white"
                        : "#e0e0e0",
                  },
                }}
              >
                <img
                  src={largeIcon?.path}
                  alt={interaction.interaction_name}
                  style={{
                    width: 20,
                    height: 20,
                    filter:
                      selectedInteraction === interaction.interaction_id
                        ? "none"
                        : "grayscale(100%)",
                    transition: "filter 0.3s ease",
                  }}
                />
              </IconButton>
            </Tooltip>
          );
        })}
      </Paper>
    </Box>
  );
};

const ItemCard = ({ image, title, isSelected, onClick }) => (
  <Card
    elevation={0}
    onClick={onClick}
    style={{
      marginBottom: "10px",
    }}
  >
    <CardActionArea
      style={{
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box
        style={{
          border: isSelected ? "1px solid #007AFF" : "none",
          height: "164px",
          width: "164px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CardMedia
          component="img"
          height="170"
          image={image}
          alt={title}
          style={{
            height: "120px",
            width: "98.28px",
            objectFit: "cover",
          }}
        />
      </Box>
      <CardContent
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography
          style={{
            fontSize: "18px",
            fontWeight: "500",
            fontFamily: "Urbanist",
            color: isSelected ? "#007AFF" : "black",
          }}
        >
          {title}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

const Main = () => {
  const [rotate, setRotate] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [showAllProducts, setShowAllProducts] = useState(false);

  const [showNewPaper, setShowNewPaper] = useState(false);
  const [showButtonsLayout, setShowButtonsLayout] = useState(false);

  const handleButtonClick = () => {
    setShowAllProducts((prev) => !prev);
    setShowNewPaper(false);
  };

  const handleIconClick = () => {
    setShowNewPaper(true);
  };
  const { id } = useParams();
  const { data } = useGetExperienceDataById(id);

  const viewActionData =
    data?.data?.experience?.collection?.items?.[0]?.interactions;

  const collectionActionData = data?.data?.experience?.collection;

  const productList = data?.data?.experience?.collection?.items;

  const initialCardItems = productList?.map((product) => {
    const image = product.item_icons.find(
      (icon) => icon.file_type === "L"
    )?.path;
    return {
      image: image || "https://i.ibb.co/2sGqztG/2.jpg",
      // image: image || "",
      title: product.item_display_short_title || "Untitled",
      long_title: product?.item_display_long_title || "Untitled",
      product_id: product?.product?.product_id,
      product: product?.product,
      item_id: product?.item_id,
      interactions: product?.interactions,
    };
  });

  const getData = data?.data;
  console.log("getData", getData);
  const controlData = getData?.experience?.controls;

  const experienceId = getData?.experience?.experience_id;
  const experienceName = getData?.experience?.experience_name || "Experience";
  // const productKey = getData?.experience?.products[0]?.product_key;
  const productKey =
    getData?.experience?.collection?.items?.[0]?.product?.product_key;
  console.log("productKey", productKey);
  const sessionId = getData?.sessionID;
  console.log("sessionId", sessionId);

  const isTablet = useMediaQuery("(max-width:960px)");

  const url = `${process.env.REACT_APP_CANVAS_URL}/?experience=${experienceId}+&product=${productKey}+&session=${sessionId}&page_height=100&page_width=100`;
  const [isIframeDocumentLoaded, setIsIframeDocumentLoaded] = useState(false);

  const URL = process.env.REACT_APP_SOCKET_URL;
  const socket = io(URL, { autoConnect: false });

  const [isSocketConnected, setIsSocketConnected] = useState(false);
  const {
    currProductKey,
    chapterList,
    currPlayMode,
    currActId,
    isLoadingScreen,
    currItemId,
  } = useSocket(socket);

  console.log("isLoadingScreen", isLoadingScreen);

  const iframeRef = useRef(null);
  const isLoadingCanvas = isLoadingScreen;
  console.log("isLoadingCanvas", isLoadingCanvas);
  const handleIframeLoad = () => {
    setIsIframeDocumentLoaded(true);
  };

  const { mutate: sendRotateCall } = useSetActionCall();
  const { mutate: changeProductCall } = useSetProductChangeCall();

  const controlId = getData?.experience?.controls?.[0]?.control_id;
  console.log("controlId", controlId);

  useEffect(() => {
    if (!isSocketConnected && sessionId) {
      socket.auth = { sessionId };
      socket.connect();
      setIsSocketConnected(true);
    }
  }, [sessionId]);

  const selectedItem =
    initialCardItems && initialCardItems.length > 0
      ? selectedIndex !== null && selectedIndex < initialCardItems.length
        ? [initialCardItems[selectedIndex]]
        : initialCardItems.find((item) => item.item_id === currItemId)
        ? [initialCardItems.find((item) => item.item_id === currItemId)]
        : [initialCardItems[0]]
      : [];

  const payloadForItemChange = {
    session_id: sessionId,
    message: {
      type: "change_item",
      message: { item_id: selectedItem?.[0]?.item_id },
    },
  };

  useEffect(() => {
    if (selectedItem.length > 0) {
      changeProductCall(payloadForItemChange);
    }
  }, [selectedIndex]);

  return (
    <Paper
      elevation={0}
      style={{ display: "flex", height: "100vh", overflow: "hidden" }}
    >
      {/* Left side */}
      <Box
        sx={{
          width: showNewPaper
            ? "97%"
            : showAllProducts
            ? isTablet
              ? "60%"
              : "65%"
            : isTablet
            ? "75%"
            : "84%",
          height: "100%",
          transition: "width 0.2s ease",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flex: 1, position: "relative" }}>
          {/* Show iframe only when isLoadingCanvas is true */}
          <iframe
            id="iframeDesktop"
            // ref={iframeRef}
            src={url}
            // onLoad={handleIframeLoad}
            scrolling="no"
            height="100%"
            width="100%"
            frameBorder={0}
            style={{
              display: isLoadingCanvas ? "visible" : "hidden",
              // position: "absolute",
              // top: 0,
              // left: 0,
            }}
          />

          {!isLoadingCanvas && (
            <Box
              sx={{
                height: "100%",
                width: "100%",
                position: "absolute",
                top: 0,
                left: 0,
                // backgroundColor: "#D3D3D3",
                zIndex: 10,
              }}
            >
              <Loading
                text="Loading..."
                waveSpeed={0.4}
                waveHeight={1}
                fontSize={32}
                color="#192b61"
                enableFlicker={true}
                flickerIntensity={3}
                flickerSpeed={3}
                isTransparent={true}
                backgroundColor="#D3D3D3"
              />
            </Box>
          )}
        </Box>

        {/* <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
  
              // border: "1px solid red",
            }}
          > */}
        <Box
          style={{
            // display: "flex",
            // justifyContent: "space-between",
            // padding: "5px",
            position: "fixed",
            bottom: "0%",
            left: "10px",
            transform: "translateY(-50%)",
            zIndex: 1000,
            gap: "10px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {showAllProducts &&
            selectedItem?.[0]?.interactions &&
            selectedItem?.[0]?.interactions.length > 0 && (
              <Views
                viewActionData={selectedItem[0].interactions}
                sessionId={sessionId}
                collectionActionData={collectionActionData}
                item={selectedItem?.[0]}
              />
            )}
        </Box>
      </Box>

      {/* Right side */}
      {!showNewPaper && (
        <Box
          sx={{
            width: showAllProducts
              ? isTablet
                ? "40%"
                : "35%"
              : isTablet
              ? "25%"
              : "16%",
            height: "100%",
            transition: "width 0.2s ease",
            position: "relative",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Paper
            elevation={3}
            style={{
              padding: "10px",
              marginBottom: "3px",
              fontSize: "24px",
              fontFamily: "Urbanist",
            }}
          >
            {showAllProducts ? selectedItem?.[0]?.long_title : experienceName}
          </Paper>
          <Paper
            elevation={3}
            sx={{
              padding: "10px",
              height: "calc(100% - 46px)",
              overflowY: "auto",
            }}
          >
            <Box sx={{ width: "100%" }}>
              {!showAllProducts && (
                <>
                  {initialCardItems?.map((item, index) => (
                    <ItemCard
                      key={index}
                      image={item.image}
                      title={item.title}
                      isSelected={selectedIndex === index}
                      onClick={() => {
                        setSelectedIndex(index);
                        // setShowAllProducts((prev) => !prev);
                      }}
                    />
                  ))}
                </>
              )}
              {showAllProducts && (
                <CollectionProductView
                  selectedItem={selectedItem}
                  sessionId={sessionId}
                />
              )}
            </Box>
          </Paper>

          {/* Add Icon to the right side of the box */}
          {showAllProducts && (
            <Box
              sx={{
                position: "absolute",
                top: "15%",
                left: "-20px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                cursor: "pointer",
              }}
              onClick={() => {
                handleIconClick();
                setShowButtonsLayout(true);
              }}
            >
              <ArrowForwardIosOutlinedIcon
                sx={{
                  fontSize: "20px",
                }}
              />
            </Box>
          )}
        </Box>
      )}

      {/* Button outside the bordered box, positioned bottom-right */}

      {!showButtonsLayout && (
        <Box
          sx={{
            position: "absolute",
            bottom: "20px", // Position it slightly above the bottom
            right: showAllProducts
              ? isTablet
                ? "41%"
                : "36%"
              : isTablet
              ? "26%"
              : "17%", // Align it to the left of the bordered box
            zIndex: 1000,
          }}
        >
          <StyledButton
            variant="outlined"
            size="small"
            onClick={handleButtonClick}
          >
            {showAllProducts ? "View All Products" : "Configure"}
          </StyledButton>
        </Box>
      )}

      {/* New Paper that shows on click */}
      {showNewPaper && (
        <Box
          sx={{
            width: "3%",
            position: "absolute",
            top: "0",
            right: "0",
            height: "100%",
            zIndex: 1000,
          }}
        >
          <Paper elevation={3} style={{ height: "100%" }}>
            <Box
              sx={{
                position: "absolute",
                top: "15%",
                left: "-20px",
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                backgroundColor: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                cursor: "pointer",
              }}
            >
              <ArrowBackIosNewOutlinedIcon
                sx={{
                  fontSize: "20px",
                }}
                onClick={() => {
                  setShowNewPaper(false);
                  setShowAllProducts(true);
                  setShowButtonsLayout(false);
                }}
              />
            </Box>
            <Box
              sx={{
                position: "absolute",
                bottom: "20px", // Position it slightly above the bottom
                right: "120%",
                zIndex: 1000,
              }}
            >
              <StyledButton
                variant="outlined"
                size="small"
                onClick={() => {
                  handleButtonClick();
                  setShowButtonsLayout(false);
                }}
              >
                {showAllProducts ? "View All Products" : "Configure"}
              </StyledButton>
            </Box>
          </Paper>
        </Box>
      )}

      <Toolbar
        rotate={rotate}
        setRotate={setRotate}
        controlId={controlId}
        sessionId={sessionId}
        sendRotateCall={sendRotateCall}
        controlData={controlData}
      />
    </Paper>
  );
};

export default Main;
